import React, { useState, useCallback, useEffect, useRef } from 'react';
import { mix } from "@spa/utils/assetPathResolver";
import { useFilter } from '@spa/features/search_job/pc/contexts/FilterContext';
import {trackClickJobFilterSearch} from "@spa/features/search_job/pc/api/analytics/trackEvent";
import {useTrack} from "@spa/features/search_job/pc/contexts/TrackContext";

/**
 * Feature単一選択のフィルターコンポーネント
 *
 * @param {Object} props
 * @param {string} props.label
 * @param {Array<{value: string|number, label: string}>} props.options
 * @param {string} props.categoryName
 * @param {string} props.featureParamKey
 * @param {string} [props.width="w230"]
 */
const SingleSelectFeatureFilter = ({
    label,
    options,
    categoryName,
    featureParamKey,
    width = "w230",
    translations,
}) => {
    const { handleFilterChange, getFilterValue } = useFilter();
    const [isOpen, setIsOpen] = useState(false);

    const initialSelectedValuesRef = useRef(getFilterValue(featureParamKey, categoryName) || []);
    const selectedValueForDisplay = initialSelectedValuesRef.current[0];
    const isActive = Boolean(selectedValueForDisplay);

    const { channelName } = useTrack();

    // options に "All Jobs" を追加
    const allJobsLabel = translations.all_jobs
    const extendedOptions = [
        { value: "0", label: allJobsLabel },
        ...options
    ];

    const filteredOptions = extendedOptions.filter(option => {
        if (option.label === allJobsLabel) {
            return isActive;
        }
        return true;
    });

    const selectedOption = extendedOptions.find(opt =>
        opt.value.toString() === (selectedValueForDisplay || '').toString()
    );
    const displayLabel = selectedOption?.label ?? label;

    const handleOptionSelect = useCallback((option) => {
        trackClickJobFilterSearch(channelName);
        if (option.label === allJobsLabel || option.value.toString() === "0") {
            handleFilterChange(featureParamKey, null, categoryName);
        } else {
            handleFilterChange(featureParamKey, option.value, categoryName);
        }
        setIsOpen(false);
    }, [handleFilterChange, featureParamKey, categoryName]);

    const handleClearClick = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        trackClickJobFilterSearch(channelName);
        // filter.js 実行後にReactのクリア処理を実行
        setTimeout(() => {
            handleFilterChange(featureParamKey, null, categoryName);
        }, 0);
    }, [handleFilterChange, featureParamKey, categoryName]);

    useEffect(() => {
        const clearBtnElement = document.querySelector(
            `.s-filter_btn_wrap.is-active[data-feature-key="${featureParamKey}"][data-category="${categoryName}"] .s-filter_clearBtn`
        );
        if (!clearBtnElement) return;

        const handleJQueryClear = (e) => {
            if (!e.detail?.reactHandled) {
                handleClearClick(new MouseEvent('mouseup', { bubbles: true }));
            }
        };

        clearBtnElement.addEventListener('click', handleJQueryClear);
        return () => {
            clearBtnElement.removeEventListener('click', handleJQueryClear);
        };
    }, [handleClearClick, featureParamKey, categoryName]);

    // モーダルの外側クリックで閉じる処理
    useEffect(() => {
        if (!isOpen) return;

        const handleOutsideClick = (e) => {
            if (!e.target.closest(`.s-filter_btn_wrap[data-feature-key="${featureParamKey}"][data-category="${categoryName}"]`)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('click', handleOutsideClick);
        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, [isOpen, featureParamKey, categoryName]);

    const wrapperClassName = [
        "p-filter_btn_wrap s-filter_btn_wrap",
        isActive && "is-active",
        isOpen && "is-on"
    ].filter(Boolean).join(" ");

    return (
        <li>
            <div
                className={wrapperClassName}
                data-feature-key={featureParamKey}
                data-category={categoryName}
            >
                <button
                    type="button"
                    className="p-filter_btn s-filter_btn"
                    aria-expanded="false"
                    aria-label={translations.item_filter.replace(':item', label)}
                    onClick={() => setIsOpen(prev => !prev)}
                >
                    {displayLabel}
                </button>
                {isActive && (
                    <button
                        type="button"
                        className="p-filter_clearBtn s-filter_clearBtn"
                        onClick={handleClearClick}
                        aria-label={translations.remove_item_filter.replace(':item', selectedOption?.label || label)}
                    >
                        <img
                            src={mix("webfront/images/icon/cross_bold.svg")}
                            className="p-filter_clearBtn_img"
                            alt={translations.remove_item_filter.replace(':item', label)}
                            width="9"
                            height="9"
                        />
                    </button>
                )}
            </div>

            <div className={`p-filter_select_wrap s-filter_menu_box${isOpen ? ' is-show' : ''}`}>
                <div
                    className={`p-filter_select_balloon p-filter_select_balloon--${width} s-filter_menu`}
                    tabIndex="0"
                    aria-label={translations.filter_options.replace(':item', label)}
                    role="listbox"
                >
                    <ul role="presentation">
                        {filteredOptions.map(option => (
                            <li key={option.value} className="p-filter_select_box">
                                <button
                                    type="button"
                                    className={`p-filter_select_item s-filter_select${selectedValueForDisplay === option.value ? ' is-selected' : ''}`}
                                    role="radio"
                                    aria-checked={selectedValueForDisplay === option.value}
                                    onClick={() => handleOptionSelect(option)}
                                >
                                    {option.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </li>
    );
};

export default SingleSelectFeatureFilter;
